import React, { useState } from "react";
import { RAreaChart, RLineChart, RBarChart } from "../../../ChartUtility/recharts";
import { scopeColorAt } from "./chartPalette";
import { EMISSION_UNIT, toTonnesRows } from "./emissionUnits";
import * as S from "./scopeDashboard.styles";

const CATEGORY = "category";
const KPI = "kpi";

/**
 * Emissions-trend panel with a By-category / By-KPI toggle. Category mode stacks
 * an area per category; KPI mode draws a line per selected KPI with an
 * auto-scaling axis and toggle chips. A scope with a single KPI drops the toggle
 * and its chips, since both groupings would plot the same series. Owns only its
 * own view state (mode + selected KPIs + hovered KPI); all data arrives via props.
 * @param {object[]} props.monthly - by-category rows { month, [category]: value }
 * @param {object[]} props.monthlyByKpi - by-KPI rows { month, [questionId]: value }
 * @param {string[]} props.categories - category keys to stack
 * @param {{questionId:number,title:string}[]} props.kpis - contribution-sorted KPIs
 * @param {string} [props.fyLabel]
 */
export const EmissionsTrendPanel = ({
  monthly = [],
  monthlyByKpi = [],
  categories = [],
  kpis = [],
  fyLabel,
}) => {
  const [mode, setMode] = useState(CATEGORY);
  const [selectedIds, setSelectedIds] = useState(() =>
    kpis.slice(0, 3).map((k) => k.questionId),
  );
  const [hoveredId, setHoveredId] = useState(null);

  const toggleKpi = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // A single time bucket can't form a line/area — fall back to bars so the
  // panel still reads cleanly (e.g. a scope with one KPI reported once).
  const singlePoint = monthly.length < 2;

  const monthlyT = toTonnesRows(monthly);
  const monthlyByKpiT = toTonnesRows(monthlyByKpi);

  // With one KPI both groupings plot the same single series, so the toggle
  // offers no real choice — drop it and show whichever grouping has data.
  const singleSeries = kpis.length <= 1;
  const activeMode = singleSeries
    ? (categories.length ? CATEGORY : KPI)
    : mode;

  const categorySeries = categories.map((key, i) => {
    const color = scopeColorAt(i);
    return {
      key,
      name: key,
      color,
      fillOpacity: 0.35,
      maxBarSize: 48,
      getColor: () => color,
    };
  });

  const kpiSeries = kpis
    .map((k, i) => ({ k, color: scopeColorAt(i) }))
    .filter(({ k }) => selectedIds.includes(k.questionId))
    .map(({ k, color }) => ({
      key: String(k.questionId),
      name: k.title || `KPI ${k.questionId}`,
      color,
      dot: true,
      strokeWidth:
        hoveredId == null ? 2 : hoveredId === k.questionId ? 3.25 : 1.25,
      maxBarSize: 48,
      getColor: () => color,
    }));

  return (
    <S.Panel>
      <S.PanelHead>
        <S.PanelTitles>
          <S.PanelTitle>Emissions trend</S.PanelTitle>
          <S.PanelSub>
            Monthly, {EMISSION_UNIT}{fyLabel ? ` · ${fyLabel}` : ""}
          </S.PanelSub>
        </S.PanelTitles>
        {!singleSeries && (
          <S.Toggle role="tablist" aria-label="Trend grouping">
            <S.ToggleBtn
              type="button"
              role="tab"
              aria-selected={mode === CATEGORY}
              $active={mode === CATEGORY}
              onClick={() => setMode(CATEGORY)}
            >
              By category
            </S.ToggleBtn>
            <S.ToggleBtn
              type="button"
              role="tab"
              aria-selected={mode === KPI}
              $active={mode === KPI}
              onClick={() => setMode(KPI)}
            >
              By KPI
            </S.ToggleBtn>
          </S.Toggle>
        )}
      </S.PanelHead>

      {activeMode === KPI && (
        <S.Chips>
          {kpis.map((k, i) => {
            const on = selectedIds.includes(k.questionId);
            const color = scopeColorAt(i);
            return (
              <S.Chip
                key={k.questionId}
                type="button"
                aria-pressed={on}
                title={k.title || ""}
                $on={on}
                $color={color}
                onClick={() => toggleKpi(k.questionId)}
                onMouseEnter={() => setHoveredId(k.questionId)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <S.ChipDot $on={on} $color={color} />
                {k.title || `KPI ${k.questionId}`}
              </S.Chip>
            );
          })}
        </S.Chips>
      )}

      <div key={activeMode}>
        {activeMode === CATEGORY ? (
          singlePoint ? (
            <RBarChart
              data={monthlyT}
              xKey="month"
              series={categorySeries}
              stacked
              height={280}
              unit={EMISSION_UNIT}
              animate
            />
          ) : (
            <RAreaChart
              data={monthlyT}
              xKey="month"
              series={categorySeries}
              stacked
              height={280}
              unit={EMISSION_UNIT}
              animate
            />
          )
        ) : singlePoint ? (
          <RBarChart
            data={monthlyByKpiT}
            xKey="month"
            series={kpiSeries}
            height={280}
            unit={EMISSION_UNIT}
            animate
          />
        ) : (
          <RLineChart
            data={monthlyByKpiT}
            xKey="month"
            series={kpiSeries}
            height={280}
            unit={EMISSION_UNIT}
            animate
          />
        )}
      </div>

      {activeMode === KPI && (
        <S.Note>Axis auto-scales to whichever KPIs are selected.</S.Note>
      )}
    </S.Panel>
  );
};
